import { GradientParser } from '../lib/gradient';
import { createGradientFill } from '../lib/gradient-parser';
import { warn } from '../lib/logger';
import seed from '../lib/seed-random';
import type { TextProvider } from '../provider/text';
import {
  LayoutSizeType,
  TEXT_DECORATION_TYPES,
  TEXT_FILL_MODE,
  TEXT_HORIZONTAL_ALIGN_TYPES,
  TEXT_VERTICAL_ALIGN_TYPES,
  TYPES,
} from './constants';
import { renderRect } from './shape';
import { isEqual } from './utils/helper';
import { resolveRadius } from './utils/radius';
import { createRasterContext, getCacheByType } from './utils/raster';
import { getShaderFillCanvas, isShaderGradientFill } from './utils/shader-fill';
import {
  charWidth,
  drawHandDrawnCircle,
  drawParallelogram,
  getLineHeightInPx,
  positionToTextIndex,
} from './utils/text';
import { getPaddingBox } from './utils/node';
import {
  deserializeStrokeLayers,
  drawMultiLayerStroke,
  resolveTextStrokes,
} from './utils/text-stroke';
import { drawGlyphWithTextFx, renderTextWithTextFx } from './utils/textFx';
import { getLineWidth, isTextSupported, wrapTextFn, WrapType } from './utils/wrap-text';
import { NodeLike } from './context/node';
import { isFontLoaded } from '../provider/font';

seed();

export { getPaddingBox };

// TextFx typing
export type TextFxName = 'bevel' | 'bubble' | 'inner-glow' | 'emboss' | 'raylight';
export type TextFxParams = Record<string, number | string | boolean>;
export interface TextFxSpec {
  type: TextFxName;
  params?: TextFxParams;
}

// Style span applied to a selection range in wrapped text indices
export interface TextStyleSpan {
  // Half-open interval [start, end) in wrapped text coordinates
  selection: [number, number];
  // Optional fill override: solid color or gradient string
  fill?: string;
  // Optional font overrides for this range
  fontWeight?: string;
  fontStyle?: string;
  // Optional text effect applied per-glyph within this range
  textFx?: string | TextFxSpec;
}

export type TextNode = NodeLike & {
  // vertical align of text content
  alignItems: TEXT_VERTICAL_ALIGN_TYPES;
  background: string;
  borderColor: string;
  borderWidth: number;
  fill: string;
  filter: string | null;
  fontFamily: string;
  fontSize: number;
  fontStyle: string;
  fontWeight: string;
  letterSpacing: number;
  lineHeight: number;
  name: string;
  opacity: number;
  padding: string;
  parentId: string;
  radius: number;
  rotation: number;
  shadow: string;
  strokes: string;
  text: string;
  // horizontal align of text content
  textAlign: TEXT_HORIZONTAL_ALIGN_TYPES;
  // node type
  type: TYPES.text;

  // layout sizing mode
  width: LayoutSizeType;
  height: LayoutSizeType;

  x: number;
  y: number;
  w: number;
  h: number;
  styles: TextStyleSpan[] | null;
  transform: any;
  // text decoration
  textDecoration?: TEXT_DECORATION_TYPES;
  // fill mode for text painting ("", char, line)
  fillMode?: TEXT_FILL_MODE;

  maxW: number | undefined;
  maxH: number | undefined;
  minW: number | undefined;
  minH: number | undefined;

  _version: number;

  // Internal cache for text layout result.
  __textLayout?: ReturnType<typeof wrapText> & {
    fontLoaded: boolean;
  };
  // Internal cache for each grapheme rendering result.
  __graphemeLayout?: LineContext['graphemes'][];

  // Optional text effect configuration; can be a parsed spec or a shorthand string like
  // "text-fx(bevel, size=0.4; h=0.4)" that will be parsed at runtime.
  textFx?: string | TextFxSpec;
  // Optional in-text decorations
  highlights?: Array<{
    type: 'rect' | 'mark' | 'wave' | 'border' | 'dot' | 'line' | 'hand-drawn-circle';
    selection: [number, number];
    fill?: string;
    // Additional params (h, x, y, size, line, radius, skewX, padding, spacing, w) are read dynamically
    [key: string]: any;
  }>;
  // Optional effect object used in renderTextWithEffect
  effect?: { type: string; params: any } | null;
};

type Grapheme = {
  width: number;
  height: number;
  /**
   * The absolute x position of the character
   * If text node is inside a container, this is relative to the container's top-left corner.
   */
  x: number;
  /**
   * The absolute y position of the character baseline.
   * If text node is inside a container, this is relative to the container's top-left corner.
   */
  y: number;
  char: string;
  font: string;
  charWidth: number;
  letterSpacing: number;
  alignSpacing: number;
};

// Line context passed around during rendering a single line
type LineContext = {
  text: string[];
  /**
   * The absolute x position of the first character in the line
   */
  x: number;
  /**
   * The absolute y position of the first character baseline in the line
   */
  y: number;
  w: number;
  h: number;
  strokes?: any;
  fill?: any;
  node: TextNode;
  index: number;
  lineIndex: number;
  lines: Array<string | string[]>;
  graphemes: Grapheme[];
  fontValue: string;
  letterSpacing: number;
  lineHeight: number;
  renderContext: any;
  curLineStartIdxInFullText: number;
};

export function layoutTextNode(node: TextNode) {
  if (node.type === TYPES.text) {
    const renderNode: TextNode = { ...node };

    const text = renderNode.text || '';
    const isWidthHug = renderNode.width === LayoutSizeType.hug;
    const isHeightHug = renderNode.height === LayoutSizeType.hug;
    const padding = getPaddingBox(renderNode);

    if (isWidthHug || isHeightHug) {
      if (isWidthHug) {
        const result = wrapText({ ...renderNode, w: 10000 }, text);
        const newW = Math.round(result.width + padding.x + 1);
        if (renderNode.w !== newW) {
          node.w = newW;
        }
      }
      if (isHeightHug) {
        const result = wrapText({ ...renderNode, w: renderNode.w }, text);
        const newH = Math.round(result.height + padding.y);
        if (text) {
          if (renderNode.h !== newH) {
            node.h = newH;
          }
        } else {
          if (renderNode.h !== 0) {
            node.h = 0;
          }
        }
      }
    }

    if (renderNode.minH) {
      node.h = Math.max(renderNode.minH, node.h);
    }
    if (renderNode.minW) {
      node.w = Math.max(renderNode.minW, node.w);
    }

    if (renderNode.maxH) {
      node.h = Math.min(renderNode.maxH, node.h);
    }

    if (renderNode.maxW) {
      node.w = Math.min(renderNode.maxW, node.w);
    }

    return node;
  }
  return node;
}

export function renderText(renderContext, node: TextNode) {
  const { context } = renderContext;

  if (node.type !== TYPES.text) return;

  const text = node.text || '';
  if (!text && !node.background && !node.borderWidth) return; // Skip empty text nodes without background/border

  const fontLoaded = isFontLoaded(node.fontFamily);
  if (!node.__textLayout) {
    node.__textLayout = {
      ...wrapText(node, text),
      fontLoaded,
    };
  } else {
    const {
      fontSettings,
      fontValue,
      safeBoxSize,
      safeWidth,
      text: originalText,
      fontLoaded: originalFontLoaded,
    } = node.__textLayout;
    const style = getTextStyle(node);

    if (
      !isEqual(style, {
        fontSettings,
        fontValue,
        safeBoxSize,
        safeWidth,
      }) ||
      originalFontLoaded !== fontLoaded ||
      text !== originalText
    ) {
      node.__textLayout = {
        ...wrapText(node, text),
        fontLoaded,
      };
    }
  }

  if (text) {
    renderTextWithContentCacheable(context, renderContext, node);
  } else {
    renderTextRaw(renderContext, node);
  }
}

// Types for render cache within renderTextWithContentCacheable
type CanvasWithRelease = HTMLCanvasElement & { release: () => void };
interface RenderConfigSpec {
  actualWidth: number;
  actualHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  scaleX: number;
  scaleY: number;
}
interface RenderTextCacheEntry {
  key: string;
  zoom: number;
  canvas: CanvasWithRelease;
  x: number;
  y: number;
  w: number;
  h: number;
  renderConfig: RenderConfigSpec;
  dispose: () => void;
}

function renderTextWithContentCacheable(
  context: CanvasRenderingContext2D,
  renderContext: any,
  node: TextNode,
) {
  const cacheName = node.id;
  const caches = getCacheByType(renderContext, 'renderText') as Map<string, RenderTextCacheEntry>;
  const existCache = caches.get(cacheName);
  const { scale } = renderContext.providers || {};
  const isEditing = renderContext.providers.text.isEditing(node);
  const text = node.text || '';
  const isLoaded = isTextSupported(node, text, renderContext);
  const isScaling = scale?.scaling || false;

  if (renderContext.mode === 'export' || isEditing || !scale || !isLoaded) {
    return renderTextRaw(renderContext, node);
  }

  // 改进的DPR计算，确保高分辨率渲染
  const deviceDPR = renderContext.dpr;
  const safeDPR = Math.max(2, deviceDPR); // 确保最小DPR为2
  const zoom = scale.scale * safeDPR;

  // 更严格的缓存匹配条件，减少模糊问题
  const isZoomMatch = existCache && Math.abs(existCache.zoom - zoom) < 0.5;
  const key = `#${node._version || 0}:${isLoaded}`;
  const enhancedKey = `${key}`;

  // 增加缓存质量检查
  const isCacheValid =
    existCache &&
    existCache.key === key &&
    existCache.canvas &&
    existCache.renderConfig &&
    existCache.renderConfig.actualWidth > 0 &&
    existCache.renderConfig.actualHeight > 0 &&
    (isZoomMatch || isScaling);

  if (isCacheValid) {
    const isScaling = scale?.scaling;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = isScaling
      ? 'low'
      : renderContext.highQuality
        ? 'high'
        : 'medium';

    context.drawImage(
      existCache.canvas,
      0,
      0,
      existCache.renderConfig.actualWidth,
      existCache.renderConfig.actualHeight, // 源区域（实际渲染区域）
      existCache.x,
      existCache.y,
      existCache.w,
      existCache.h, // 目标区域
    );
    return;
  }

  if (existCache && existCache.canvas) {
    existCache.canvas.release();
  }

  const renderText = node.__textLayout;

  if (!renderText) {
    return;
  }

  const isOverHeight = renderText.height > node.h;
  const fontSetting = getFontSetting(node);
  const fontSize = node.fontSize || 12;
  const lineHeight = getLineHeightInPx(fontSize, fontSetting.lineHeight) * 0.5;

  const renderNode = {
    ...node,
    h: Math.max(node.h, renderText.height),
  };

  renderNode.y -= lineHeight / 2;
  renderNode.h += lineHeight;
  renderNode.x -= lineHeight / 2;
  renderNode.w += lineHeight;

  const {
    canvas,
    context: cacheContext,
    renderConfig,
  } = createRasterContext(renderContext, renderNode, zoom) as {
    canvas: CanvasWithRelease;
    context: CanvasRenderingContext2D;
    renderConfig: RenderConfigSpec;
  };
  const cache: RenderTextCacheEntry = {
    key: enhancedKey,
    zoom,
    canvas: canvas,
    y: node.y - lineHeight / 2,
    h: node.h + lineHeight,
    x: node.x - lineHeight / 2,
    w: node.w + lineHeight,
    renderConfig,
    dispose: () => {
      cache.canvas?.release();
    },
  };

  // 保存原始变换状态
  cacheContext.save();

  // 设置高质量渲染参数
  const isScalingNow = scale?.scaling;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = isScalingNow
    ? 'low'
    : renderContext.highQuality
      ? 'high'
      : 'medium';

  if (isOverHeight) {
    const alignItems = node.alignItems || TEXT_VERTICAL_ALIGN_TYPES.top;
    const delta = renderText.height - node.h;

    switch (alignItems) {
      case TEXT_VERTICAL_ALIGN_TYPES.top:
        // For top alignment, extend downward
        cache.h += delta;
        break;
      case TEXT_VERTICAL_ALIGN_TYPES.center:
        // For center alignment, extend equally up and down
        cache.y -= delta / 2;
        cache.h += delta;
        cacheContext.translate(0, delta / 2);
        break;
      case TEXT_VERTICAL_ALIGN_TYPES.bottom:
        // For bottom alignment, extend upward
        cache.y -= delta;
        cache.h += delta;
        cacheContext.translate(0, delta);
        break;
      default:
        // Default to top alignment
        cache.h += delta;
        break;
    }
  }

  const fakeRenderContext = {
    ...renderContext,
    context: cacheContext,
  };

  renderTextRaw(fakeRenderContext, node);

  // 恢复变换状态
  cacheContext.restore();

  caches.set(cacheName, cache);

  // 使用高质量绘制参数
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = isScalingNow
    ? 'low'
    : renderContext.highQuality
      ? 'high'
      : 'medium';

  // 使用实际渲染尺寸绘制到主画布，确保渲染一致性
  context.drawImage(
    cache.canvas,
    0,
    0,
    renderConfig.actualWidth,
    renderConfig.actualHeight, // 源区域（实际渲染区域）
    cache.x,
    cache.y,
    cache.w,
    cache.h, // 目标区域
  );
}

function renderTextRaw(renderContext, node: TextNode) {
  const { context } = renderContext;

  const text = node.text || '';
  if (!text && !node.background && !node.borderWidth) return; // Skip empty text nodes without background/border

  // Apply text-fx as an integrated step: render glyphs offscreen then composite
  if (node.textFx && !renderContext.__textFxActive) {
    return renderTextWithTextFx(renderContext, node, renderTextRaw);
  }

  const padding = getPaddingBox(node);
  const rawX = node.x;
  const rawY = node.y;
  const x = rawX + padding.left;
  let y = rawY + padding.top;

  const w = node.w;
  const h = node.h;

  const renderText = node.__textLayout;

  if (!renderText) {
    warn(`Text layout is not found when rendering text node [${node.id}]`);
    return;
  }

  const lines = renderText.lines;
  const breakType = renderText.linesBreakType;

  const fontSize = node.fontSize || 12;
  const fill = node.fill || 'black';
  const textAlign = node.textAlign || TEXT_HORIZONTAL_ALIGN_TYPES.left;
  const alignItems = node.alignItems || TEXT_VERTICAL_ALIGN_TYPES.top;
  const fontSetting = getFontSetting(node);
  const fontValue = createFontConfig(node);

  // Set font only once
  context.font = fontValue;
  const lineHeight = getLineHeightInPx(fontSize, fontSetting.lineHeight);

  // Calculate vertical alignment only once
  if (lines.length > 0) {
    const totalHeight = lines.length * lineHeight;
    const contentHeight = Math.max(0, h - padding.top - padding.bottom);
    if (alignItems === TEXT_VERTICAL_ALIGN_TYPES.center) {
      // Center within the actual content area even if h < lineHeight
      y = rawY + padding.top + (contentHeight - totalHeight) / 2;
    } else if (alignItems === TEXT_VERTICAL_ALIGN_TYPES.bottom) {
      // Align bottom within the actual content area
      y = rawY + padding.top + (contentHeight - totalHeight);
    } else {
      // Top alignment
      y = rawY + padding.top;
    }
  }

  // Draw background if needed
  if (node.background) {
    createRect(context, {
      x: rawX,
      y: rawY,
      w,
      h,
      radius: node.radius,
    });
    const bg = node.background;
    if (GradientParser.is(bg)) {
      GradientParser.create(context, bg, { x, y, w, h }, () => {
        context.fill();
      });
    } else {
      context.fillStyle = bg;
      context.fill();
    }
  }

  // Text rendering setup
  context.textAlign = 'left';
  context.textBaseline = 'middle';
  const topY = y;
  const letterSpacing = node.letterSpacing || 0;
  const textDecoration = node.textDecoration;
  const strokeSize = Math.max(2, fontSize / 14);

  // Cache common calculations for text alignment
  const halfWidth = w / 2;
  const paddingOffset = (padding.right - padding.left) / 2;
  const strokes = resolveTextStrokes(node);

  node.__graphemeLayout = [];

  // Render each line of text
  if (lines.length) {
    let curLineStartIdxInFullText = 0;
    const inlineStyles = node.styles ?? [];
    const findStyleInRange = (range: [number, number]) => {
      const [start, end] = range;
      return inlineStyles.filter((s) => start < s.selection[1] && end > s.selection[0]);
    };
    const getFinalFontStyle = (
      charIdx: number,
      styles: TextStyleSpan[],
      baseStyle: {
        fontStyle: string;
        fontWeight: string;
      },
    ) => {
      let finalStyle: { fontStyle?: string; fontWeight?: string } = baseStyle;

      styles.forEach((style) => {
        if (charIdx >= style.selection[0] && charIdx < style.selection[1]) {
          finalStyle = finalStyle
            ? {
                ...finalStyle,
                ...style,
              }
            : style;
        }
      });

      return createFontConfig(node, finalStyle);
    };
    const graphemesLayout: LineContext['graphemes'][] = [];

    lines.forEach((line, index) => {
      // Normalize current line (grapheme array -> string) for measurement and painting
      const lineY = topY + lineHeight * index + lineHeight * 0.5;
      if (index > 0) {
        curLineStartIdxInFullText +=
          lines[index - 1].length + (breakType[index] === WrapType.HARD ? 1 : 0);
      }
      const matchedStyles = findStyleInRange([
        curLineStartIdxInFullText,
        curLineStartIdxInFullText + line.length,
      ]);

      let textX: number = 0;
      let lineWidth = 0;
      let justifyExtraSpace = 0;

      const graphemes: Grapheme[] = [];

      {
        line.forEach((char, idx) => {
          const finalFontStyle = getFinalFontStyle(curLineStartIdxInFullText + idx, matchedStyles, {
            fontStyle: node.fontStyle,
            fontWeight: node.fontWeight,
          });
          const curCharWidth = charWidth.calculate(char, finalFontStyle);
          const actualCharWidth = curCharWidth + letterSpacing;

          lineWidth += actualCharWidth;
          graphemes.push({
            x: 0,
            y: lineY,
            char,
            width: actualCharWidth,
            height: lineHeight,
            charWidth: curCharWidth,
            font: finalFontStyle,
            letterSpacing,
            alignSpacing: 0,
          });
        });

        const last = graphemes[graphemes.length - 1];
        if (last) {
          last.letterSpacing = 0;
          last.width = last.charWidth;
          lineWidth -= letterSpacing;
        }
      }

      switch (textAlign) {
        case TEXT_HORIZONTAL_ALIGN_TYPES.center:
          textX = x + halfWidth - paddingOffset - lineWidth / 2 - padding.left;
          break;
        case TEXT_HORIZONTAL_ALIGN_TYPES.right:
          textX = x + w - lineWidth - padding.right - padding.left;
          break;
        case TEXT_HORIZONTAL_ALIGN_TYPES.justify: {
          textX = x;

          // Distribute remaining width across all interior gaps on non-last lines when multi-line
          const contentWidth = w - padding.left - padding.right;
          const extraWidth = contentWidth - lineWidth;
          const isLastLine = index === lines.length - 1;
          const multiLine = lines.length > 1;
          const shouldJustify = extraWidth > 0 && !(isLastLine && multiLine);

          if (shouldJustify) {
            const charGaps = Math.max(0, line.length - 1);
            if (charGaps > 0) {
              justifyExtraSpace = extraWidth / charGaps;
            }
          }
          break;
        }
        case TEXT_HORIZONTAL_ALIGN_TYPES.left:
        default:
          textX = x;
          break;
      }

      // update x for each grapheme
      {
        let curX = textX;
        graphemes.forEach((metric) => {
          metric.x = curX;
          metric.alignSpacing = justifyExtraSpace;
          curX += metric.width + justifyExtraSpace;
        });
      }

      const lineContext: LineContext = {
        text: line,
        graphemes: graphemes,
        x: textX,
        y: lineY,
        w: lineWidth,
        h: lineHeight,
        strokes,
        fill,
        node,
        index,
        lineIndex: index,
        lines,
        fontValue,
        letterSpacing,
        lineHeight,
        renderContext,
        curLineStartIdxInFullText,
        // No special handling for spaces under justify
      };

      graphemesLayout.push(graphemes);

      // Render text with effects
      renderTextWithEffect(context, lineContext);

      // Draw text decorations if needed
      if (textDecoration) {
        context.beginPath();
        if (textDecoration === TEXT_DECORATION_TYPES.underline) {
          context.moveTo(textX, lineY + fontSize);
          context.lineTo(textX + lineWidth, lineY + fontSize);
        } else if (textDecoration === TEXT_DECORATION_TYPES.lineThrough) {
          context.moveTo(textX, lineY + lineHeight * 0.5 - 4);
          context.lineTo(textX + lineWidth, lineY + lineHeight * 0.5 - 4);
        }
        context.strokeStyle = fill;
        context.lineWidth = strokeSize;
        context.stroke();
        context.closePath();
      }
    });

    node.__graphemeLayout = graphemesLayout;
  }

  // Draw border if needed
  if (node.borderWidth) {
    renderRect(renderContext, {
      x: rawX,
      y: rawY,
      w,
      h,
      radius: node.radius,
      fill: 'transparent',
      strokeWidth: node.borderWidth,
      stroke: node.borderColor,
    });
  }
}

function createRect(context, { x, y, w, h, radius }) {
  context.beginPath();
  if (radius) {
    const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(w, h, radius);
    context.moveTo(x + topLeft, y);
    context.lineTo(x + w - topRight, y);
    context.arcTo(x + w, y, x + w, y + topRight, topRight);
    context.lineTo(x + w, y + h - bottomRight);
    context.arcTo(x + w, y + h, x + w - bottomRight, y + h, bottomRight);
    context.lineTo(x + bottomLeft, y + h);
    context.arcTo(x, y + h, x, y + h - bottomLeft, bottomLeft);
    context.lineTo(x, y + topLeft);
    context.arcTo(x, y, x + topLeft, y, topLeft);
  } else {
    context.rect(x, y, w, h);
  }
  context.closePath();
}

function renderTextWithHightlight(context: CanvasRenderingContext2D, lineContext: LineContext) {
  const { text, lineIndex, graphemes, y, node, strokes, lineHeight } = lineContext;
  const highlights = node.highlights;
  if (!highlights || highlights.length === 0) return;

  const layers = deserializeStrokeLayers(strokes);
  const firstLayer = layers[0] || null;
  const strokeWidth = firstLayer?.width ?? 0;
  const fontSize = node.fontSize + strokeWidth * 2;

  // Cache common calculations
  const fontSizeMultiplier = {
    h: (highlight) => fontSize * (highlight.h ?? 0.4),
    x: (highlight) => fontSize * (highlight.x ?? 0),
    y: (highlight) => fontSize * (highlight.y ?? 0),
    size: (highlight) => fontSize * (highlight.size ?? 0.4),
    line: (highlight) => fontSize * (highlight.line ?? 0.2),
    radius: (highlight) => fontSize * (highlight.radius ?? 0.1),
    skewX: (highlight) => fontSize * (highlight.skewX ?? 0.2),
    padding: (highlight) => (highlight.padding || 0.2) * fontSize,
  };

  const [lineStart, lineEnd] = [
    positionToTextIndex(node, [lineIndex, 0]),
    positionToTextIndex(node, [lineIndex, text.length]),
  ];

  highlights.forEach((highlight) => {
    const [hStart, hEnd] = highlight.selection || [0, 0];

    // Skip if selection is out of range
    if (hEnd <= lineStart || hStart >= lineEnd) {
      return;
    }

    // Clamp selection to valid range [start, end)
    const validStart = Math.max(0, hStart - lineStart);
    const validEnd = Math.min(text.length, hEnd - lineStart);

    const highlightedText = graphemes.slice(validStart, validEnd);
    if (!highlightedText.length) return;

    // Account for justify distributed space before the highlight start
    const highlightedTextX = graphemes[validStart]!.x;

    // Account for justify distributed space within the highlighted range
    const highlightedTextWidth = highlightedText.reduce((acc, cur) => {
      return acc + cur.width;
    }, 0);
    const highlightedTextY = y;
    const height = fontSizeMultiplier.h(highlight);
    const xOffset = fontSizeMultiplier.x(highlight);
    const yOffset = fontSizeMultiplier.y(highlight);

    const adjustedNode = {
      x: highlightedTextX + xOffset,
      y: highlightedTextY + yOffset,
      w: highlightedTextWidth,
      h: height,
    };

    context.fillStyle = createGradientFill(context, highlight.fill || '#000', adjustedNode) || '';
    context.strokeStyle = createGradientFill(context, highlight.fill || '#000', adjustedNode) || '';

    switch (highlight.type) {
      case 'rect': {
        const height = fontSizeMultiplier.h(highlight);
        const xOffset = fontSizeMultiplier.x(highlight);
        const yOffset = fontSizeMultiplier.y(highlight);
        context.fillRect(
          highlightedTextX + xOffset,
          highlightedTextY + yOffset,
          highlightedTextWidth,
          height,
        );
        break;
      }
      case 'mark': {
        const height = fontSizeMultiplier.h(highlight);
        const yOffset = fontSizeMultiplier.y(highlight);
        const skewX = fontSizeMultiplier.skewX(highlight);
        const radius = fontSizeMultiplier.radius(highlight);
        const rect = {
          x: highlightedTextX - skewX,
          y: highlightedTextY + yOffset,
          w: highlightedTextWidth + skewX,
          h: height,
        };
        drawParallelogram(context, rect, skewX, radius);
        break;
      }
      case 'wave': {
        const waveStartY = highlightedTextY + fontSizeMultiplier.y(highlight);
        const waveStartX = highlightedTextX + fontSizeMultiplier.x(highlight);
        const waveEndX = highlightedTextX + highlightedTextWidth;
        const waveWidthSize = fontSizeMultiplier.size(highlight);
        const waveLineSize = fontSizeMultiplier.line(highlight);

        context.beginPath();
        context.lineJoin = 'round';
        context.moveTo(waveStartX, waveStartY);
        for (let i = waveStartX; i < waveEndX; i += waveWidthSize) {
          context.quadraticCurveTo(
            i + waveWidthSize / 2,
            waveStartY + waveLineSize,
            Math.min(i + waveWidthSize, waveEndX),
            waveStartY,
          );
        }
        context.lineWidth = waveLineSize;
        context.stroke();
        context.closePath();
        break;
      }
      case 'border': {
        const lineWidth = (highlight.size || 0.2) * fontSize;
        const padding = fontSizeMultiplier.padding(highlight);
        const yOffset = fontSizeMultiplier.y(highlight);
        const xOffset = fontSizeMultiplier.x(highlight);

        context.beginPath();
        context.lineWidth = lineWidth;
        context.rect(
          highlightedTextX - padding + xOffset,
          yOffset + highlightedTextY - fontSize * 0.5 - padding,
          highlightedTextWidth + padding * 2,
          fontSize + padding,
        );
        context.stroke();
        context.closePath();
        break;
      }
      case 'dot': {
        const dotRadius = (highlight.size || 0.1) * fontSize;
        const yOffset = (highlight.y || 0.5) * fontSize;
        const xOffset = (highlight.x || 0) * fontSize;
        const dotSpacing = dotRadius * (2 + (highlight.spacing ?? 1));
        const startX = highlightedTextX + xOffset;
        const endX = startX + highlightedTextWidth;

        for (let i = startX; i < endX; i += dotSpacing) {
          context.beginPath();
          context.arc(i, highlightedTextY + yOffset, dotRadius, 0, Math.PI * 2);
          context.fill();
          context.closePath();
        }
        break;
      }
      case 'line': {
        const itemWidth = (highlight.w || 0.36) * fontSize;
        const itemHeight = (highlight.h || 0.15) * fontSize;
        const yOffset = (highlight.y || 0.6) * fontSize;
        const xOffset = (highlight.x || 0) * fontSize;
        const dotSpacing = itemWidth * (0.5 + (highlight.spacing ?? 1));
        const startX = highlightedTextX + xOffset;
        const endX = startX + highlightedTextWidth;

        for (let i = startX; i < endX; i += dotSpacing) {
          context.fillRect(i, highlightedTextY + yOffset - itemHeight, itemWidth, itemHeight);
        }
        break;
      }
      case 'hand-drawn-circle': {
        drawHandDrawnCircle(
          context,
          highlightedTextX,
          highlightedTextY,
          highlightedTextWidth,
          lineHeight,
          fontSize,
          highlight,
        );
        break;
      }
    }
  });
}

const defaultTextColor = '#111';

function renderTextWithStyle(context, lineContext: LineContext) {
  const {
    text,
    lineIndex,
    graphemes,
    x,
    y,
    fill,
    node,
    fontValue,
    letterSpacing,
    renderContext,
    curLineStartIdxInFullText,
  } = lineContext;
  // Normalize current line text (grapheme array -> string)
  const styles = node.styles;
  const fillMode = node.fillMode || ''; // '' | 'char' | 'line'

  const isGlobalFillMode = fillMode !== TEXT_FILL_MODE.char && fillMode !== TEXT_FILL_MODE.line;

  const resolveGlobalFill = () => {
    if (!isGlobalFillMode) return null;
    if (GradientParser.is(fill)) {
      return GradientParser.create(context, fill, {
        x: node.x,
        y: node.y,
        w: node.w,
        h: node.h,
      });
    }
    return fill || defaultTextColor;
  };

  const globalFill = resolveGlobalFill();

  // helpers scoped to this function to avoid duplication
  const computeStyledRect = (style, charAdvance, effectiveFontForStyle) => {
    if (!style || !style.selection) {
      return { x, w: charAdvance };
    }
    const styleStart = (style?.selection?.[0] ?? 0) - curLineStartIdxInFullText;
    const styleEnd = (style?.selection?.[1] ?? 0) - curLineStartIdxInFullText;
    const styleWidth =
      styleEnd > styleStart
        ? getStyledSegmentWidth(
            text.slice(styleStart, styleEnd),
            curLineStartIdxInFullText + styleStart,
            node,
            effectiveFontForStyle || fontValue,
            letterSpacing,
          )
        : charAdvance;
    const styleStartX =
      x +
      (styleStart > 0
        ? getStyledSegmentWidth(
            text.slice(0, styleStart),
            curLineStartIdxInFullText,
            node,
            fontValue,
            letterSpacing,
          )
        : 0);
    return { x: styleStartX, w: styleWidth };
  };

  const drawChar = (char: Grapheme, currentX, measureFont) => {
    // If char-level text-fx present in style, draw via glyph pipeline
    if (styles && styles.length > 0) {
      const curIndex = positionToTextIndex(node, [lineIndex, text.indexOf(char.char)]);
      const s = styles.find((s) => {
        const sel = s.selection || [0, 0];
        return curIndex >= (sel[0] || 0) && curIndex < (sel[1] || 0);
      });
      const fx = s?.textFx;
      if (fx) {
        drawGlyphWithTextFx(
          lineContext,
          context,
          char,
          currentX,
          y,
          s?.fill ?? fill,
          measureFont || fontValue,
          fx,
        );
        return;
      }
    }
    renderTextWithManualSpacing(
      lineContext,
      context,
      [char],
      currentX,
      y,
      letterSpacing,
      measureFont,
    );
  };

  const paintWithFill = (
    candidateFill,
    currentX,
    charAdvance,
    defaultRectForSelection,
    char: Grapheme,
    measureFont,
  ) => {
    // Shader-based fill using masking
    if (isShaderGradientFill(candidateFill)) {
      const rect = (() => {
        if (fillMode === TEXT_FILL_MODE.char) {
          return {
            x: currentX,
            y,
            w: Math.max(1, Math.floor(charAdvance)),
            h: Math.max(1, Math.floor(lineContext.h)),
          };
        } else if (fillMode === TEXT_FILL_MODE.line) {
          return {
            x,
            y,
            w: Math.max(1, Math.floor(lineContext.w)),
            h: Math.max(1, Math.floor(lineContext.h)),
          };
        }
        return {
          x: defaultRectForSelection.x,
          y,
          w: Math.max(1, Math.floor(defaultRectForSelection.w)),
          h: Math.max(1, Math.floor(lineContext.h)),
        };
      })();

      const shaderResult = getShaderFillCanvas(renderContext, candidateFill, rect.w, rect.h);
      if (!shaderResult || !shaderResult.canvas) {
        // Fallback to color/global fill path if shader unavailable
        context.fillStyle = globalFill || defaultTextColor;
        drawChar(char, currentX, measureFont);
        return;
      }

      // Build mask with the glyph(s)
      const targetW = shaderResult.canvas.width;
      const targetH = shaderResult.canvas.height;
      const scaleX = targetW / Math.max(1, rect.w);
      const scaleY = targetH / Math.max(1, rect.h);
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = targetW;
      maskCanvas.height = targetH;
      const maskCtx = maskCanvas.getContext('2d') as CanvasRenderingContext2D;
      maskCtx.save();
      maskCtx.scale(scaleX, scaleY);
      maskCtx.font = measureFont || fontValue;
      maskCtx.textBaseline = 'middle';
      maskCtx.fillStyle = '#ffffff';

      if (fillMode === TEXT_FILL_MODE.char) {
        // Draw single character centered vertically
        const localX = Math.max(0, Math.floor(currentX - rect.x));
        const localY = Math.floor(rect.h / 2);
        maskCtx.fillText(char.char, localX, localY);
      } else {
        // Draw whole line segment with manual spacing
        const localStartX = Math.max(0, Math.floor(x - rect.x));
        const localY = Math.floor(rect.h / 2);
        renderTextWithManualSpacing(
          lineContext,
          maskCtx,
          graphemes,
          localStartX,
          localY,
          letterSpacing,
          measureFont,
        );
      }
      maskCtx.restore();

      // Composite shader result with mask
      const compCanvas = document.createElement('canvas');
      compCanvas.width = targetW;
      compCanvas.height = targetH;
      const compCtx = compCanvas.getContext('2d') as CanvasRenderingContext2D;
      compCtx.drawImage(shaderResult.canvas, 0, 0, targetW, targetH);
      compCtx.globalCompositeOperation = 'destination-in';
      compCtx.drawImage(maskCanvas, 0, 0);
      compCtx.globalCompositeOperation = 'source-over';

      // Draw back to main context at target position
      const prevSmooth = context.imageSmoothingEnabled;
      const prevQuality = context.imageSmoothingQuality;
      const isScalingNow = !!(
        renderContext.providers &&
        renderContext.providers.scale &&
        renderContext.providers.scale.scaling
      );
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = isScalingNow
        ? 'low'
        : renderContext.highQuality
          ? 'high'
          : 'medium';
      context.drawImage(compCanvas, 0, 0, targetW, targetH, rect.x, rect.y, rect.w, rect.h);
      context.imageSmoothingEnabled = prevSmooth;
      if (prevQuality) context.imageSmoothingQuality = prevQuality;
      return;
    }
    if (GradientParser.is(candidateFill)) {
      let rect;
      if (fillMode === TEXT_FILL_MODE.char) {
        rect = { x: currentX, y, w: charAdvance, h: lineContext.h };
      } else if (fillMode === TEXT_FILL_MODE.line) {
        rect = { x, y, w: lineContext.w, h: lineContext.h };
      } else {
        rect = {
          x: defaultRectForSelection.x,
          y,
          w: defaultRectForSelection.w,
          h: lineContext.h,
        };
      }
      GradientParser.create(context, candidateFill, rect, () => {
        drawChar(char, currentX, measureFont);
      });
    } else {
      context.fillStyle = candidateFill || globalFill || defaultTextColor;
      drawChar(char, currentX, measureFont);
    }
  };

  // Set font only once
  if (styles && styles.length > 0) {
    const len = graphemes.length;

    for (let i = 0; i < len; i++) {
      const char = graphemes[i];
      const charIdx = positionToTextIndex(node, [lineIndex, i]);
      // Determine effective font for this character considering styled overrides
      const style = styles.find((s) => charIdx >= s.selection[0] && charIdx < s.selection[1]);

      // Apply font override for drawing
      context.font = char.font;

      const rectForDefault = computeStyledRect(style, char.width, char.font);

      if (style?.fill != null || style?.textFx) {
        if (style?.textFx) {
          // Draw effect directly at glyph level to honor styled selection
          drawGlyphWithTextFx(
            lineContext,
            context,
            char.char,
            char.x,
            y,
            style?.fill ?? fill,
            char.font,
            style.textFx,
          );
        } else {
          // draw with fill
          paintWithFill(style.fill, char.x, char.width, rectForDefault, char, char.font);
        }
      } else {
        if (globalFill) {
          context.fillStyle = globalFill || defaultTextColor;
          drawChar(char, char.x, char.font);
        } else {
          paintWithFill(fill, char.x, char.width, rectForDefault, char, char.font);
        }
      }

      context.font = fontValue;
    }
  } else {
    // Global branch: support dynamic-shader for fill
    if (isShaderGradientFill(fill)) {
      const mode = fillMode;
      if (mode === TEXT_FILL_MODE.char) {
        // Per-character shader fill
        const currentX = x;
        const chars = graphemes;
        for (let i = 0; i < chars.length; i++) {
          const { char: ch, x, y, width: w, height: h } = chars[i];
          const rect = {
            x,
            y,
            w,
            h,
          };
          const shaderResult = getShaderFillCanvas(renderContext, fill, rect.w, rect.h);
          if (shaderResult && shaderResult.canvas) {
            const targetW = shaderResult.canvas.width;
            const targetH = shaderResult.canvas.height;
            const scaleX = targetW / Math.max(1, rect.w);
            const scaleY = targetH / Math.max(1, rect.h);
            const maskCanvas = document.createElement('canvas');
            maskCanvas.width = targetW;
            maskCanvas.height = targetH;
            const mctx = maskCanvas.getContext('2d') as CanvasRenderingContext2D;
            mctx.scale(scaleX, scaleY);
            mctx.font = fontValue;
            mctx.textBaseline = 'middle';
            mctx.fillStyle = '#fff';
            mctx.fillText(ch, 0, Math.floor(rect.h / 2));
            const compCanvas = document.createElement('canvas');
            compCanvas.width = targetW;
            compCanvas.height = targetH;
            const cctx = compCanvas.getContext('2d') as CanvasRenderingContext2D;
            cctx.drawImage(shaderResult.canvas, 0, 0, targetW, targetH);
            cctx.globalCompositeOperation = 'destination-in';
            cctx.drawImage(maskCanvas, 0, 0);
            cctx.globalCompositeOperation = 'source-over';
            const prevSmooth = context.imageSmoothingEnabled;
            const prevQuality = context.imageSmoothingQuality;
            const isScalingNow = !!(
              renderContext.providers &&
              renderContext.providers.scale &&
              renderContext.providers.scale.scaling
            );
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = isScalingNow
              ? 'low'
              : renderContext.highQuality
                ? 'high'
                : 'medium';
            context.drawImage(compCanvas, 0, 0, targetW, targetH, rect.x, rect.y, rect.w, rect.h);
            context.imageSmoothingEnabled = prevSmooth;
            if (prevQuality) context.imageSmoothingQuality = prevQuality;
          } else {
            context.fillStyle = defaultTextColor;
            context.fillText(ch, currentX, y);
          }
        }
      } else if (mode === TEXT_FILL_MODE.line) {
        const rect = {
          x,
          y,
          w: Math.max(1, Math.floor(lineContext.w)),
          h: Math.max(1, Math.floor(lineContext.h)),
        };
        const shaderResult = getShaderFillCanvas(renderContext, fill, rect.w, rect.h);
        if (shaderResult && shaderResult.canvas) {
          const targetW = shaderResult.canvas.width;
          const targetH = shaderResult.canvas.height;
          const scaleX = targetW / Math.max(1, rect.w);
          const scaleY = targetH / Math.max(1, rect.h);
          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = targetW;
          maskCanvas.height = targetH;
          const mctx = maskCanvas.getContext('2d') as CanvasRenderingContext2D;
          mctx.scale(scaleX, scaleY);
          mctx.font = fontValue;
          mctx.textBaseline = 'middle';
          mctx.fillStyle = '#fff';
          renderTextWithManualSpacing(
            lineContext,
            mctx,
            graphemes,
            0,
            Math.floor(rect.h / 2),
            letterSpacing,
          );
          const compCanvas = document.createElement('canvas');
          compCanvas.width = targetW;
          compCanvas.height = targetH;
          const cctx = compCanvas.getContext('2d') as CanvasRenderingContext2D;
          cctx.drawImage(shaderResult.canvas, 0, 0, targetW, targetH);
          cctx.globalCompositeOperation = 'destination-in';
          cctx.drawImage(maskCanvas, 0, 0);
          const prevSmooth = context.imageSmoothingEnabled;
          const prevQuality = context.imageSmoothingQuality;
          const isScalingNow = !!(
            renderContext.providers &&
            renderContext.providers.scale &&
            renderContext.providers.scale.scaling
          );
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = isScalingNow
            ? 'low'
            : renderContext.highQuality
              ? 'high'
              : 'medium';
          context.drawImage(compCanvas, 0, 0, targetW, targetH, rect.x, rect.y, rect.w, rect.h);
          context.imageSmoothingEnabled = prevSmooth;
          if (prevQuality) context.imageSmoothingQuality = prevQuality;
        } else {
          renderTextWithManualSpacing(lineContext, context, graphemes, x, y, letterSpacing);
        }
      } else {
        // Global area based on node box
        const rect = {
          x: node.x,
          y: node.y,
          w: Math.max(1, Math.floor(node.w)),
          h: Math.max(1, Math.floor(node.h)),
        };
        const shaderResult = getShaderFillCanvas(renderContext, fill, rect.w, rect.h);
        if (shaderResult && shaderResult.canvas) {
          const targetW = shaderResult.canvas.width;
          const targetH = shaderResult.canvas.height;
          const scaleX = targetW / Math.max(1, rect.w);
          const scaleY = targetH / Math.max(1, rect.h);
          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = targetW;
          maskCanvas.height = targetH;
          const mctx = maskCanvas.getContext('2d') as CanvasRenderingContext2D;
          mctx.scale(scaleX, scaleY);
          mctx.font = fontValue;
          mctx.textBaseline = 'middle';
          mctx.fillStyle = '#fff';
          // Draw each line at its local position inside node box
          mctx.save();
          // Shift to local coordinates of node box
          mctx.translate(-(node.x - 0), -(node.y - 0));
          // Render current line only to avoid overdraw from other lines handled in their turn
          renderTextWithManualSpacing(lineContext, mctx, graphemes, x, y, letterSpacing);
          mctx.restore();
          const compCanvas = document.createElement('canvas');
          compCanvas.width = targetW;
          compCanvas.height = targetH;
          const cctx = compCanvas.getContext('2d') as CanvasRenderingContext2D;
          cctx.drawImage(shaderResult.canvas, 0, 0, targetW, targetH);
          cctx.globalCompositeOperation = 'destination-in';
          cctx.drawImage(maskCanvas, 0, 0);
          const prevSmooth = context.imageSmoothingEnabled;
          const prevQuality = context.imageSmoothingQuality;
          const isScalingNow = !!(
            renderContext.providers &&
            renderContext.providers.scale &&
            renderContext.providers.scale.scaling
          );
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = isScalingNow
            ? 'low'
            : renderContext.highQuality
              ? 'high'
              : 'medium';
          context.drawImage(compCanvas, 0, 0, targetW, targetH, rect.x, rect.y, rect.w, rect.h);
          context.imageSmoothingEnabled = prevSmooth;
          if (prevQuality) context.imageSmoothingQuality = prevQuality;
        } else {
          renderTextWithManualSpacing(lineContext, context, graphemes, x, y, letterSpacing);
        }
      }
    } else if (GradientParser.is(fill)) {
      if (fillMode === TEXT_FILL_MODE.char) {
        renderTextPerCharGradient(lineContext, context, text, x, y, letterSpacing, fill);
      } else if (fillMode === TEXT_FILL_MODE.line) {
        const rect = { x, y, w: lineContext.w, h: lineContext.h };
        GradientParser.create(context, fill, rect, () => {
          renderTextWithManualSpacing(lineContext, context, graphemes, x, y, letterSpacing);
        });
      } else {
        context.fillStyle = globalFill;
        renderTextWithManualSpacing(lineContext, context, graphemes, x, y, letterSpacing);
      }
    } else {
      context.fillStyle = fill;
      renderTextWithManualSpacing(lineContext, context, graphemes, x, y, letterSpacing);
    }
  }
}

function renderTextWithSelection(context, lineContext: LineContext) {
  const { graphemes, x, y, lineHeight, node, lineIndex, renderContext } = lineContext;
  if (!renderContext.providers.text.isEditing(node)) return;
  const textProvider = renderContext.providers.text as TextProvider;
  // Render cursor if editing
  // Calculate cursor position based on textProvider.cursorPosition (map through diff if present)
  const focusPosition = textProvider.focusPosition as readonly [number, number];
  const isCaretOnThisLine = focusPosition[0] === lineIndex;

  if (isCaretOnThisLine && focusPosition[1] <= graphemes.length) {
    const atLineEnd = focusPosition[1] === graphemes.length;
    const curGrapheme = atLineEnd ? graphemes[graphemes.length - 1] : graphemes[focusPosition[1]];
    const cursorVisPos = curGrapheme
      ? atLineEnd
        ? curGrapheme.x + curGrapheme.width
        : curGrapheme.x
      : x;

    const cursorX = cursorVisPos;
    const cursorY = y - lineHeight / 2;

    // 计算闪动动画
    const time = renderContext.providers.time.now();
    const blinkCycle = 1000; // 1秒一个周期
    const blinkProgress = (time % blinkCycle) / blinkCycle;
    const isVisible = blinkProgress < 0.5; // 前半个周期显示，后半个周期隐藏

    if (isVisible) {
      // 简约清晰的光标样式，保持屏幕像素宽度与缩放无关
      const prevStrokeStyle = context.strokeStyle;
      const prevLineWidth = context.lineWidth;
      const prevLineCap = context.lineCap;

      const scaleProvider = renderContext.providers && renderContext.providers.scale;
      const dpr = renderContext.dpr || window.devicePixelRatio || 1;
      const scaleFactor = (scaleProvider && scaleProvider.scale) || 1;
      const zoom = Math.max(0.0001, scaleFactor * dpr);
      const desiredPx = 6; // 期望的屏幕像素宽度

      context.strokeStyle = '#333333';
      context.lineWidth = desiredPx / zoom;
      context.lineCap = 'round';

      context.beginPath();
      context.moveTo(cursorX, cursorY);
      context.lineTo(cursorX, cursorY + lineHeight);
      context.stroke();

      // 恢复上下文
      context.strokeStyle = prevStrokeStyle;
      context.lineWidth = prevLineWidth;
      context.lineCap = prevLineCap;
    }
  }

  if (
    textProvider.isCollapsed ||
    graphemes.length === 0 ||
    textProvider.inputController?.isComposing
  ) {
    return;
  }

  const [start, end] = textProvider.normalizedSelection as [
    readonly [number, number],
    readonly [number, number],
  ];

  if (lineIndex < start[0] || lineIndex > end[0]) {
    return;
  }

  const firstGrapheme = lineIndex === start[0] ? graphemes[start[1]] : graphemes[0];
  const lastGrapheme =
    lineIndex === end[0] ? graphemes[end[1] - 1] : graphemes[graphemes.length - 1];

  const lastGraphemeOfCurLine = graphemes[graphemes.length - 1];

  const highlightedTextX = firstGrapheme
    ? firstGrapheme.x
    : lastGraphemeOfCurLine.x + lastGraphemeOfCurLine.width;
  const highlightedTextY = firstGrapheme?.y ?? lastGraphemeOfCurLine.y;
  const highlightedTextWidth = lastGrapheme
    ? lastGrapheme.x + lastGrapheme.width - highlightedTextX
    : 0;

  context.fillStyle = 'rgba(0, 0, 255, 0.2)';
  context.fillRect(
    highlightedTextX,
    highlightedTextY - lineHeight / 2,
    highlightedTextWidth,
    lineHeight,
  );
}

function renderTextWithEffect(context, lineContext: LineContext) {
  const { text, graphemes, x, y, fill, node, renderContext, strokes, letterSpacing } = lineContext;
  const effect = node.effect;

  renderTextWithHightlight(context, lineContext);

  if (effect) {
    const type = effect.type;
    if (type === 'stack') {
      const fontSize = node.fontSize;
      effect.params.layers.forEach((layer) => {
        const offsetX = (layer.x || 0) * fontSize;
        const offsetY = (layer.y || 0) * fontSize;

        // Ensure consistent rendering context for each layer
        context.save();

        // Handle multi-layer strokes for each layer
        if (layer.strokes) {
          drawMultiLayerStroke(
            renderContext,
            context,
            text,
            layer.strokes,
            x + offsetX,
            y + offsetY,
            lineContext,
          );
        } else if (layer.stroke) {
          const strokes = resolveTextStrokes(layer, node);
          drawMultiLayerStroke(
            renderContext,
            context,
            text,
            strokes,
            x + offsetX,
            y + offsetY,
            lineContext,
          );
        }

        const layerFill = layer.fill || fill || defaultTextColor;
        if (GradientParser.is(layerFill)) {
          const fillMode = node.fillMode || '';
          if (fillMode === TEXT_FILL_MODE.char) {
            // Per-character gradient in effect layer
            for (let i = 0; i < graphemes.length; i++) {
              const { char: ch, x, y } = graphemes[i];
              const w = charWidth.calculate(ch, lineContext.fontValue);
              GradientParser.create(
                context,
                layerFill,
                { x: x + offsetX, y: y + offsetY, w, h: lineContext.h },
                () => {
                  context.fillText(ch, x + offsetX, y + offsetY);
                },
              );
            }
          } else {
            const rect =
              fillMode === TEXT_FILL_MODE.line
                ? { x, y, w: lineContext.w, h: lineContext.h }
                : { x, y, w: node.w, h: node.h };
            GradientParser.create(context, layerFill, rect, () => {
              // Use manual character spacing for consistency
              renderTextWithManualSpacing(
                lineContext,
                context,
                graphemes,
                x + offsetX,
                y + offsetY,
                letterSpacing,
              );
            });
          }
        } else {
          context.fillStyle = layerFill || 'tranparent';
          // Use manual character spacing for consistency
          renderTextWithManualSpacing(
            lineContext,
            context,
            graphemes,
            x + offsetX,
            y + offsetY,
            letterSpacing,
          );
        }

        context.restore();
      });
    } else if (type === 'behindLayer') {
      const stepY = node.fontSize * effect.params.y;
      const stepX = node.fontSize * effect.params.x;
      context.fillStyle = effect.params.fill;

      // Use manual character spacing for consistency
      renderTextWithManualSpacing(
        lineContext,
        context,
        graphemes,
        x + stepX,
        y + stepY,
        letterSpacing,
      );
    }
  }

  if (strokes) {
    drawMultiLayerStroke(renderContext, context, text, strokes, x, y, lineContext);
  }

  renderTextWithStyle(context, lineContext);
  renderTextWithSelection(context, lineContext);
}

// Helper function for manual character spacing to ensure cross-browser consistency
function renderTextWithManualSpacing(
  lineContext: LineContext,
  context: CanvasRenderingContext2D,
  graphemes: Grapheme[],
  x: number,
  y: number,
  _letterSpacing = 0,
  _measureFontOverride?: string,
) {
  // Handle emoji and surrogate pairs properly
  for (let i = 0; i < graphemes.length; i++) {
    const { char, x, y } = graphemes[i];
    context.fillText(char, x, y);
  }

  // debug
  // context.fillStyle = 'rgba(255, 0,0, .5)'
  // context.letterSpacing = `${letterSpacing || 0}px`
  // context.fillText(text, x, y);
}

// Render text with per-character gradient fill
function renderTextPerCharGradient(
  lineContext: LineContext,
  context: CanvasRenderingContext2D,
  text: string[],
  x: number,
  y: number,
  _letterSpacing = 0,
  gradient?: any,
) {
  const currentX = x;
  const { graphemes } = lineContext;
  for (let i = 0; i < graphemes.length; i++) {
    const { char: ch, x, y } = graphemes[i];
    const w = charWidth.calculate(ch, lineContext.fontValue);
    GradientParser.create(context, gradient, { x: currentX, y, w, h: lineContext.h }, () => {
      context.fillText(ch, x, y);
    });
  }
}

export function drawTextDebugLine(context, sx, sy, ex, ey, color = 'red') {
  context.beginPath();
  context.moveTo(sx, sy);
  context.lineTo(ex, ey);
  context.strokeStyle = color;
  context.lineWidth = 1;
  context.stroke();
  context.closePath();
}

export function getFontSetting(node) {
  const fontFamily = node.fontFamily || 'Noto Sans SC';
  return {
    fontFamily: `"${fontFamily}"`,
    lineHeight: node.lineHeight ?? 1.35,
    letterSpacing: node.letterSpacing || 0,
    padding: getPaddingBox(node),
  };
}

// Measure text width with per-character style overrides based on node.styles selections.
function getStyledSegmentWidth(
  text: string[],
  /**
   * The start index of current text line in the full node text.
   */
  globalStartIndexInWrapped: number,
  node: TextNode,
  fontValue: string,
  letterSpacing: number,
) {
  if (!text) return 0;
  const styles = Array.isArray(node.styles) ? node.styles : [];
  if (styles.length === 0) {
    return getLineWidth(text, fontValue, letterSpacing);
  }
  let width = 0;
  const chars = text;
  for (let i = 0; i < chars.length; i++) {
    const globalIndex = globalStartIndexInWrapped + i;
    let effectiveFont = fontValue;
    const s = styles.find((s) => {
      const sel = s.selection || [0, 0];
      return globalIndex >= (sel[0] || 0) && globalIndex < (sel[1] || 0);
    });
    if (s && (s.fontWeight || s.fontStyle)) {
      effectiveFont = createFontConfig(node, {
        fontWeight: s.fontWeight,
        fontStyle: s.fontStyle,
      });
    }
    const w = charWidth.calculate(chars[i], effectiveFont);
    width += w + (i < chars.length - 1 ? letterSpacing || 0 : 0);
  }
  return width;
}

export function getTextStyle(node: TextNode, withoutFallback: boolean = false) {
  const fontSettings = getFontSetting(node);
  const fontValue = createFontConfig(node, { withoutFallback });
  const safeBoxSize = getSafeSize(node);
  const safeWidth = Math.max(1, safeBoxSize.w);

  return {
    fontSettings,
    fontValue,
    safeBoxSize,
    safeWidth,
  };
}

export function wrapText(node: TextNode, sourceText: string) {
  sourceText = typeof sourceText === 'string' ? sourceText || '' : '';
  const { fontSettings, fontValue, safeBoxSize, safeWidth } = getTextStyle(node);

  return {
    fontSettings,
    fontValue,
    safeBoxSize,
    safeWidth,
    text: sourceText,
    ...wrapTextFn(sourceText, fontValue, safeWidth, fontSettings),
  };
}

function getSafeSize(node) {
  const padding = getPaddingBox(node);
  return {
    w: node.w - padding.x,
    h: node.h - padding.y,
  };
}

export function createFontConfig(
  node,
  { fontWeight = '', fontStyle = '', withoutFallback = false } = {},
) {
  fontWeight = fontWeight || node.fontWeight || '';
  fontStyle = fontStyle || node.fontStyle || '';
  const fontSize = node.fontSize || 16;
  const fontSettings = getFontSetting(node);
  const list = [
    `${fontSize}px ${fontSettings.fontFamily} ${withoutFallback ? '' : ', "Noto Sans"'}`,
  ];
  if (fontWeight) {
    list.unshift(fontWeight);
  }
  if (fontStyle) {
    list.unshift(fontStyle);
  }
  return list.join(' ');
}
